import { DatePicker, Form, Input, Modal, Select, Space } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { ACTIVITY_TYPE_OPTIONS } from "../utils/activityTypes";

const CreateActivityForm = ({ open, onCreate, onCancel }) => {
	const [form] = Form.useForm();
	const { TextArea } = Input;
	const [disableTextArea, setDisableTextArea] = useState(false);
	// Issue #69: 送信中は OK を受け付けない。押せたままだと、レスポンスを待つ間の
	// 連打でそのまま2件目以降が登録される（同じ日の勤怠が重複して作られる）。
	//
	// 判定を state だけで行うと、state の反映は次のレンダー以降のため、同じティックで
	// 続けて押された分を弾けない（validateFields が非同期なので、先行クリックが
	// setSubmitting へ到達する前に後続が入ってくる）。押された瞬間に同期で閉じる
	// ref をガードにし、state はボタンの見た目（loading）専用にする。
	const submittingRef = useRef(false);
	const [submitting, setSubmitting] = useState(false);
	// Issue #88: モーダルを開いた直後に最初の入力欄（活動日）へフォーカスを移し、
	// キーボードだけで入力を始められるようにする。forceRender で中身が開く前から
	// mount されているため autoFocus 属性は初回 mount 時しか効かない。open の
	// 変化を見て明示的に focus する。
	const activityDateRef = useRef(null);

	useEffect(() => {
		// Set initial state based on the default value of activityType
		const initialValue = form.getFieldValue("activityType");
		setDisableTextArea(initialValue === 0 || initialValue === 2);
	}, [form]);

	const handleActivityTypeChange = (value) => {
		setDisableTextArea(value === 0 || value === 2);
	};

	useEffect(() => {
		if (!open) return undefined;
		const timer = setTimeout(() => activityDateRef.current?.focus(), 0);
		return () => clearTimeout(timer);
	}, [open]);

	// Filter `option.label` match the user type `input`
	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const disabledDate = (current) => {
		// Get the current month and year
		const currentDate = new Date();
		const currentMonth = currentDate.getMonth();
		const currentYear = currentDate.getFullYear();
		const isFirstFiveDays = currentDate.getDate() <= 10;
		// Get the month and year of the selected date
		const selectedMonth = current.month();
		const selectedYear = current.year();

		if (isFirstFiveDays) {
			// Enable all days for the last month
			return (
				!(currentMonth === current.month() && currentYear === current.year()) &&
				!(
					currentMonth === 0 &&
					selectedMonth === 11 &&
					currentYear - 1 === selectedYear
				) &&
				!(currentMonth - 1 === selectedMonth && currentYear === selectedYear)
			);
		}

		// Disable the date if it's not in the current month or year
		return !(currentMonth === selectedMonth && currentYear === selectedYear);
	};

	return (
		<Modal
			forceRender
			// Issue #88: 幅 1020px 固定ではノート PC・画面分割など横幅の狭い環境で
			// モーダルが見切れる。画面幅に追従させ、広い画面では従来と同じ 1020px を上限にする。
			width="94vw"
			style={{ maxWidth: 1020 }}
			open={open}
			title="Add Activity"
			okText="Create"
			cancelText="Cancel"
			confirmLoading={submitting}
			cancelButtonProps={{ disabled: submitting }}
			closable={!submitting}
			maskClosable={!submitting}
			onCancel={() => {
				form.resetFields();
				setDisableTextArea(false);
				onCancel();
			}}
			onOk={() => {
				if (submittingRef.current) return;
				submittingRef.current = true;
				setSubmitting(true);
				form
					.validateFields()
					.then(async (values) => {
						// リセットの順序は従来のまま（送信前にフォームを空にする）
						form.resetFields();
						setDisableTextArea(false);
						await onCreate(values);
					})
					.catch((info) => {
						console.log("Validate Failed:", info);
					})
					.finally(() => {
						submittingRef.current = false;
						setSubmitting(false);
					});
			}}
		>
			<Form
				form={form}
				layout="vertical"
				name="form_in_modal"
				initialValues={{
					modifier: "public",
				}}
			>
				<Form.Item
					name="activityDate"
					label="Choose activity date"
					rules={[{ required: true, message: "Please select a date." }]}
				>
					<Space direction="vertical">
						<DatePicker
							ref={activityDateRef}
							onChange={(date, dateString) =>
								form.setFieldsValue({ activityDate: dateString })
							}
							disabledDate={disabledDate}
							format="YYYY-MM-DD"
						/>
					</Space>
				</Form.Item>
				<Form.Item
					name="activityType"
					label="Activity Type"
					rules={[
						{ required: true, message: "Please select an activity type." },
					]}
				>
					<Select
						showSearch
						placeholder="Select activity type"
						optionFilterProp="children"
						filterOption={filterOption}
						onChange={handleActivityTypeChange}
						options={ACTIVITY_TYPE_OPTIONS}
					/>
				</Form.Item>

				<Form.Item name="Activity" label="Activity">
					<TextArea
						style={{
							height: 120,
							resize: "none",
						}}
						placeholder="Log today's activity here (optional)"
						disabled={disableTextArea}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default CreateActivityForm;
